import { Link, useLocation } from 'react-router-dom'

import { useFilterSearch } from '@/lib/useGlobalFilters'

export interface TabItem {
  label: string
  to: string
  /** Match the path exactly, so a section landing tab is not active on every sub-page. */
  end?: boolean
}

/**
 * A section's in-page tabs. They are navigation (each is a route), so they are
 * links with `aria-current`, not an ARIA tablist. The global filters travel
 * with them. `active` names the tab to mark when the location alone cannot say
 * (a landing page that is also a tab, a detail page under one).
 */
export function TabNav({ label, items, active }: { label: string; items: readonly TabItem[]; active?: string }) {
  const search = useFilterSearch()
  const { pathname } = useLocation()
  if (items.length === 0) return null

  const isActive = (item: TabItem) =>
    active !== undefined
      ? item.to === active
      : item.end
        ? pathname === item.to
        : pathname === item.to || pathname.startsWith(`${item.to}/`)

  return (
    <nav className="ci-tabs" aria-label={label}>
      <ul>
        {items.map((item) => {
          const current = isActive(item)
          return (
            <li key={item.to}>
              <Link
                to={{ pathname: item.to, search }}
                className={current ? 'ci-tabs__link is-active' : 'ci-tabs__link'}
                aria-current={current ? 'page' : undefined}
              >
                {item.label}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

export interface SegmentOption<T extends string> {
  value: T
  label: string
}

/** A small single-choice switch inside a card (Today / 7d / 30d). */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: readonly SegmentOption<T>[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div className="ci-segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          className="ci-segmented__option"
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
